// src/components/SceltaNoleggiatore.js
// Noleggiatore del noleggio (W439): si sceglie dall'Elenco Fornitori del Centro Acquisti —
// prima quelli spuntati «Noleggiatore», poi gli altri — mai scritto a mano, così lo stesso
// fornitore ha un'unica scheda e il resoconto per fornitore non si spezza su nomi simili.
// Chi gestisce l'Elenco Fornitori (titolare, amministratori) può aggiungerlo al volo,
// con ragione sociale e P.IVA come nell'elenco; gli altri chiedono all'ufficio acquisti.
import React, { useId, useMemo, useState } from "react";
import { collection, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../utils/Toast";
import { messaggioErrore } from "../utils/erroriUtente";
import { fornitoriPerNoleggio } from "../utils/noleggiUtils";

const inp = { width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box", background: "#fff" };

/**
 * valore: fornitoreId · onScegli({ id, denominazione }) · fornitori: elenco dell'azienda
 * puoAggiungere: può aggiungere un fornitore all'elenco · orgId
 */
export default function SceltaNoleggiatore({ valore, nomeAttuale = "", onScegli, fornitori = [], puoAggiungere = false, orgId, style }) {
  const { addToast } = useToast();
  const id = useId();
  const [nuovo, setNuovo] = useState(null); // { denominazione, piva }
  const [salvo, setSalvo] = useState(false);
  const { noleggiatori, altri } = useMemo(() => fornitoriPerNoleggio(fornitori), [fornitori]);
  const scelto = fornitori.find(f => f.id === valore);

  const scegli = (fid) => {
    if (fid === "__nuovo") { setNuovo({ denominazione: "", piva: "" }); return; }
    const f = fornitori.find(x => x.id === fid);
    onScegli(f ? { id: f.id, denominazione: f.denominazione } : { id: "", denominazione: "" });
  };

  const aggiungi = async () => {
    const den = nuovo.denominazione.trim(), piva = nuovo.piva.trim().replace(/\s/g, "");
    if (!den) { addToast("Scrivi la ragione sociale del noleggiatore", "error"); return; }
    if (!piva) { addToast("La P.IVA è obbligatoria: serve a non avere lo stesso fornitore due volte", "error"); return; }
    const doppio = fornitori.find(f => !f.eliminato && String(f.piva || "").replace(/\s/g, "") === piva);
    setSalvo(true);
    try {
      if (doppio) {
        // già in elenco: si sceglie quello e lo si segna come noleggiatore
        if (!doppio.noleggiatore) await updateDoc(doc(db, "fornitori", doppio.id), { noleggiatore: true, updatedAt: serverTimestamp() });
        onScegli({ id: doppio.id, denominazione: doppio.denominazione });
        addToast(`P.IVA già in elenco: scelto ${doppio.denominazione}`, "info");
      } else {
        const idUnico = Math.max(0, ...fornitori.map(f => parseInt(f.idUnico) || 0)) + 1;
        const ref = await addDoc(collection(db, "fornitori"), {
          denominazione: den, piva, referente: "", telefono: "", cellulare: "", email: "", sconto: "", note: "",
          noleggiatore: true, idUnico, organizationId: orgId, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
        onScegli({ id: ref.id, denominazione: den });
        addToast(`${den} aggiunto all'Elenco Fornitori come noleggiatore`, "success");
      }
      setNuovo(null);
    } catch (e) { addToast(messaggioErrore(e), "error"); }
    setSalvo(false);
  };

  return (
    <div style={style}>
      <label htmlFor={`${id}-sel`} style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Noleggiatore *</label>
      <select id={`${id}-sel`} value={scelto ? valore : ""} onChange={e => scegli(e.target.value)} style={inp}>
        <option value="">— Scegli il noleggiatore —</option>
        {noleggiatori.length > 0 && <optgroup label="Noleggiatori">{noleggiatori.map(f => <option key={f.id} value={f.id}>{f.denominazione}</option>)}</optgroup>}
        {altri.length > 0 && <optgroup label="Altri fornitori">{altri.map(f => <option key={f.id} value={f.id}>{f.denominazione}</option>)}</optgroup>}
        {puoAggiungere && <option value="__nuovo">➕ Aggiungi un noleggiatore all'elenco…</option>}
      </select>
      {!scelto && nomeAttuale && <div style={{ fontSize: 11, color: "#b45309", marginTop: 3 }}>⚠️ «{nomeAttuale}» non è nell'Elenco Fornitori: sceglilo dall'elenco.</div>}
      {!puoAggiungere && <div style={{ fontSize: 11, color: "#6d28d9", marginTop: 3 }}>💜 Non c'è? Chiedi all'ufficio acquisti di aggiungerlo all'Elenco Fornitori (Centro Acquisti → Fornitori).</div>}
      {nuovo && (
        <div role="group" aria-label="Nuovo noleggiatore" style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: 10, marginTop: 6 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#5b21b6", marginBottom: 6 }}>Nuovo noleggiatore nell'Elenco Fornitori</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input aria-label="Ragione sociale del noleggiatore" value={nuovo.denominazione} onChange={e => setNuovo(n => ({ ...n, denominazione: e.target.value }))} placeholder="Ragione sociale *" style={{ ...inp, flex: "2 1 180px", width: "auto" }} />
            <input aria-label="P.IVA del noleggiatore" value={nuovo.piva} onChange={e => setNuovo(n => ({ ...n, piva: e.target.value }))} placeholder="P.IVA *" style={{ ...inp, flex: "1 1 120px", width: "auto" }} />
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-secondary" onClick={() => setNuovo(null)} style={{ fontSize: 12, padding: "5px 12px" }}>Annulla</button>
            <button type="button" className="btn btn-primary" disabled={salvo} onClick={aggiungi} style={{ fontSize: 12, padding: "5px 12px" }}>{salvo ? "Salvo…" : "Aggiungi e scegli"}</button>
          </div>
          <div style={{ fontSize: 10.5, color: "#6b7280", marginTop: 4 }}>Gli altri dati (referente, telefono, email) si completano poi nell'Elenco Fornitori.</div>
        </div>
      )}
    </div>
  );
}
